import type { ActionContext } from 'vuex'
import type { RootState } from '@/store'
import { $http } from '@/utils/https'

// The signed-in user's latest marketing decision per channel, as the journal has it: `true`
// agreed, `false` refused, `null` never asked. Counted by the e-mail and phone on the profile, over
// every answer so far - checkout and profile alike. Keys are the journal's names (`messenger`,
// singular), i.e. MARKETING_CONSENT_CHANNELS' `apiKey`.
//
// Authorized users only: both endpoints answer 401 without a token. Guests are asked at checkout
// every time and never touch this module.
export type MarketingConsentAnswers = {
  email: boolean | null
  sms: boolean | null
  messenger: boolean | null
}

export const MARKETING_CONSENTS_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
}

interface State {
  answers: MarketingConsentAnswers | null
  status: string
}

const state: State = {
  answers: null,
  status: MARKETING_CONSENTS_STATUS.IDLE,
}

const getters = {
  marketingConsentAnswers: (state: State) => state.answers,
  marketingConsentsStatus: (state: State) => state.status,
}

const actions = {
  // Never rejects. A caller that could not learn the answers falls back to asking, which is safe:
  // a fresh answer only adds rows to the journal, and the latest one wins.
  FETCH_MARKETING_CONSENTS: async ({ commit }: ActionContext<State, RootState>) => {
    commit('SET_STATUS', MARKETING_CONSENTS_STATUS.LOADING)
    try {
      const response = await $http.get<{ data: MarketingConsentAnswers }>('v1/user/marketing-consents')
      commit('SET_ANSWERS', response.data.data)
    } catch (e) {
      console.warn('marketing-consents: could not read the current answers', e)
      commit('SET_STATUS', MARKETING_CONSENTS_STATUS.FAILED)
    }
  },

  // The body is the checkout's own: marketing_consent_{email,sms,messengers} plus the version, as
  // built by buildMarketingConsentPayload(). The answer is the new state, so no second GET.
  UPDATE_MARKETING_CONSENTS: async ({ commit }: ActionContext<State, RootState>, payload: object) => {
    const response = await $http.put<{ data: MarketingConsentAnswers }>('v1/user/marketing-consents', payload)
    commit('SET_ANSWERS', response.data.data)
    return response.data.data
  },
}

const mutations = {
  SET_ANSWERS(state: State, answers: MarketingConsentAnswers) {
    state.answers = answers
    state.status = MARKETING_CONSENTS_STATUS.LOADED
  },
  SET_STATUS(state: State, status: string) {
    state.status = status
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
